import { useState, type ReactNode } from 'react'
import { Check, ChevronDown, Copy } from 'lucide-react'
import { cn } from '@/app/lib/cn'

/**
 * The shared body of every operator-facing error screen.
 *
 * Presentational only — no router hooks, no stores — because one of its callers
 * is the last-resort boundary outside the router, and because an error screen
 * that depends on the thing that just broke is how an error page itself goes
 * blank. It imports nothing that can fail at render beyond React and icons.
 */
export function ErrorPanel({
  tone,
  icon,
  eyebrow,
  title,
  children,
  notes,
  actions,
  report,
  at,
  layout,
}: {
  /** `danger` for a crash, `brand` for something benign such as a wrong address. */
  tone: 'danger' | 'brand'
  icon: ReactNode
  eyebrow: string
  title: string
  /** The explanation, in plain words. */
  children: ReactNode
  /** Short reassurances — what is still safe, what to do after reloading. */
  notes?: { icon: ReactNode; text: ReactNode }[]
  actions: ReactNode
  /** Full technical report. Omitted for errors that are not bugs. */
  report?: string
  /** When it happened, shown next to the details. */
  at?: Date
  /**
   * `outlet` fills the space under the console's banners; `screen` owns the
   * whole viewport, for when the shell itself is what failed.
   */
  layout: 'outlet' | 'screen'
}) {
  return (
    <div
      role="alert"
      className={cn(
        'relative flex flex-col overflow-y-auto',
        layout === 'screen' ? 'h-full bg-base' : 'min-h-0 flex-1',
      )}
    >
      {/* A soft wash of the tone behind the heading, so the screen reads as a
          deliberate state of the console rather than a page that failed to load. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[380px]"
        style={{
          background:
            tone === 'danger'
              ? 'radial-gradient(55% 100% at 50% 0%, rgba(255,107,87,.11), transparent 70%)'
              : 'radial-gradient(55% 100% at 50% 0%, rgba(0,179,167,.13), transparent 70%)',
        }}
      />

      {/* `m-auto` rather than centring on the parent: it centres when there is
          room and scrolls from the top when there is not, so an open details
          panel on a 768px console never pushes the buttons out of reach. */}
      <div className="citaria-step-enter relative m-auto w-full max-w-[580px] px-6 py-10 sm:px-8">
        <div
          className={cn(
            'mb-6 grid h-16 w-16 place-items-center rounded-[20px] ring-1',
            tone === 'danger'
              ? 'bg-danger/12 text-danger ring-danger/30'
              : 'bg-brand/12 text-brand ring-brand/30',
          )}
        >
          {icon}
        </div>

        <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-faint">
          {eyebrow}
        </p>
        <h1 className="mt-2 text-[26px] font-semibold leading-tight tracking-tight text-ink sm:text-[30px]">
          {title}
        </h1>
        <div className="mt-3 text-[15px] leading-relaxed text-ink-muted">{children}</div>

        {notes && notes.length > 0 && (
          <ul className="mt-6 space-y-2">
            {notes.map((note, index) => (
              <li
                key={index}
                className="flex items-start gap-3 rounded-[12px] bg-surface px-4 py-3 text-[14px] leading-snug text-ink-muted ring-1 ring-line"
              >
                <span className="mt-[1px] shrink-0 text-brand">{note.icon}</span>
                <span>{note.text}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">{actions}</div>

        {report && <TechnicalDetails report={report} at={at} />}
      </div>
    </div>
  )
}

/**
 * Collapsed by default: the operator needs the buttons, not a stack trace. Open
 * it, or just press copy — the report is copied whether or not it is visible.
 */
function TechnicalDetails({ report, at }: { report: string; at?: Date }) {
  const [open, setOpen] = useState(false)
  const [copy, setCopy] = useState<'idle' | 'done' | 'failed'>('idle')

  const copyReport = async () => {
    try {
      if (!navigator.clipboard) throw new Error('clipboard unavailable')
      await navigator.clipboard.writeText(report)
      setCopy('done')
      setTimeout(() => setCopy('idle'), 2500)
    } catch {
      // Refused by the browser, or not a secure context. Show the text so it
      // can be selected by hand instead of pretending it worked.
      setCopy('failed')
      setOpen(true)
    }
  }

  return (
    <div className="mt-10 border-t border-line pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="inline-flex min-h-[48px] items-center gap-2 text-[14px] text-ink-muted"
        >
          <ChevronDown
            size={16}
            className={cn('transition-transform duration-150', open && 'rotate-180')}
          />
          Detail teknis
          {at && (
            <span className="text-ink-faint">
              · {at.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => void copyReport()}
          className="inline-flex min-h-[48px] items-center gap-2 rounded-[10px] px-3 text-[14px] text-ink-muted active:bg-raised"
        >
          {copy === 'done' ? <Check size={16} className="text-ok" /> : <Copy size={16} />}
          {copy === 'done' ? 'Tersalin' : 'Salin detail'}
        </button>
      </div>

      {copy === 'failed' && (
        <p className="mb-2 text-[13px] text-warn">
          Tidak bisa menyalin otomatis — pilih teks di bawah, lalu salin.
        </p>
      )}

      {open && (
        <pre className="max-h-[260px] select-text overflow-auto whitespace-pre-wrap break-words rounded-[12px] bg-surface p-4 font-mono text-[12px] leading-relaxed text-ink-muted ring-1 ring-line">
          {report}
        </pre>
      )}
    </div>
  )
}
